/*
  Shared feature loader for interaction/visibility delays.
  Step 1 registers the API only — existing facades still load themselves.
*/
window.BoltFeatureLoader = window.BoltFeatureLoader || {
  loaded: {},
  pending: {},

  get isDesignMode() {
    return Boolean(
      document.body?.dataset.designMode === "true" ||
        (window.Shopify && window.Shopify.designMode),
    );
  },

  loadScript(src, id) {
    if (!src) return Promise.resolve();
    if (id && this.loaded[id]) return Promise.resolve();
    if (id && this.pending[id]) return this.pending[id];

    const existing = (id && document.getElementById(id)) || document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if (id) this.loaded[id] = true;
      return Promise.resolve();
    }

    const promise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = src;
      if (id) script.id = id;
      script.async = true;
      script.onload = () => {
        if (id) this.loaded[id] = true;
        resolve();
      };
      script.onerror = reject;
      document.head.appendChild(script);
    }).finally(() => {
      if (id) delete this.pending[id];
    });

    if (id) this.pending[id] = promise;
    return promise;
  },

  loadStylesheet(href, id) {
    if (!href) return Promise.resolve();
    if (id && this.loaded[id]) return Promise.resolve();
    if (id && this.pending[id]) return this.pending[id];

    const existing = (id && document.getElementById(id)) || document.querySelector(`link[href="${href}"]`);
    if (existing) {
      if (id) this.loaded[id] = true;
      return Promise.resolve();
    }

    const promise = new Promise((resolve, reject) => {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = href;
      if (id) link.id = id;
      link.onload = () => {
        if (id) this.loaded[id] = true;
        resolve();
      };
      link.onerror = reject;
      document.head.appendChild(link);
    }).finally(() => {
      if (id) delete this.pending[id];
    });

    if (id) this.pending[id] = promise;
    return promise;
  },

  onFirstInteraction(callback) {
    let fired = false;

    const run = () => {
      if (fired) return;
      fired = true;
      callback();
      window.removeEventListener("pointerdown", run);
      window.removeEventListener("keydown", run);
    };

    window.addEventListener("pointerdown", run, { once: true, passive: true });
    window.addEventListener("keydown", run, { once: true });
  },

  whenVisible(selector, callback, rootMargin = "300px 0px") {
    const elements = document.querySelectorAll(selector);
    if (!elements.length) return;

    if (!("IntersectionObserver" in window)) {
      elements.forEach(callback);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          callback(entry.target);
        });
      },
      { rootMargin },
    );

    elements.forEach((element) => observer.observe(element));
  },
};

(() => {
  const loader = window.BoltFeatureLoader;
  const src = window.BoltAssets && window.BoltAssets.sliders;
  if (!loader || !src) return;
  if (customElements.get("carousel-component")) return;

  const hydrate = () => loader.loadScript(src, "bolt-sliders-js");

  if (loader.isDesignMode || document.querySelector("carousel-component:not([data-lazy-slider])")) {
    hydrate();
    return;
  }

  loader.whenVisible("[data-lazy-slider]", hydrate);
  window.addEventListener("dialog:open", (event) => {
    if (event.detail?.dialog?.id !== "cart-drawer") return;
    hydrate();
  });
})();

/*
  Section CSS/JS loader (data-section-css / data-section-js)

  Default: load when the element is ~600px from the viewport.
  Eager (immediate): dialog, #cart-drawer, or data-section-load="eager"
  Skipped: [data-facade-js] (wishlist, recently viewed, newsletter, WhatsApp)
  Design mode: everything loads immediately.

  When to add data-section-load="eager" on the same element as data-section-js:
  - JS must work on the FIRST click/tap (handlers register only after the file loads)
  - Element is NOT inside a <dialog> or #cart-drawer (those are already eager)
  - Examples: custom off-canvas not using <dialog>, header tool that needs its
    section JS before open, fixed UI that IntersectionObserver never sees

  Do not use eager for normal below-fold sections (FAQ, marquee, etc.).
  Facaded dialogs must not rely on data-section-js or they will eager-load.
*/
(() => {
  const designMode =
    document.body?.dataset.designMode === "true" ||
    Boolean(window.Shopify && window.Shopify.designMode);

  const loadStyles = (el) => {
    const value = el.dataset.sectionCss;
    if (!value) return Promise.resolve();

    const tasks = value.split(",").map((href) => {
      const url = href.trim();
      if (!url || document.querySelector(`link[href="${url}"]`)) {
        return Promise.resolve();
      }

      return new Promise((resolve) => {
        const link = document.createElement("link");
        link.href = url;
        link.rel = "stylesheet";
        link.onload = resolve;
        link.onerror = resolve;
        document.head.appendChild(link);
      });
    });

    return Promise.all(tasks);
  };

  const loadScripts = (el) => {
    const value = el.dataset.sectionJs;
    if (!value) return Promise.resolve();

    const tasks = value.split(",").map((src) => {
      const url = src.trim();
      if (!url || document.querySelector(`script[src="${url}"]`)) {
        return Promise.resolve();
      }

      return new Promise((resolve) => {
        const script = document.createElement("script");
        script.src = url;
        script.defer = true;
        script.onload = resolve;
        script.onerror = resolve;
        document.head.appendChild(script);
      });
    });

    return Promise.all(tasks);
  };

  const loadSectionAssets = async (el) => {
    if (
      !el ||
      el.dataset.sectionAssetsLoaded === "true" ||
      el.dataset.sectionAssetsLoading === "true"
    ) {
      return;
    }

    el.dataset.sectionAssetsLoading = "true";

    try {
      await loadStyles(el);
      await loadScripts(el);
      el.dataset.sectionAssetsLoaded = "true";
    } finally {
      delete el.dataset.sectionAssetsLoading;
    }
  };

  const targets = document.querySelectorAll("[data-section-css], [data-section-js]");
  if (!targets.length) return;

  if (designMode) {
    targets.forEach(loadSectionAssets);
    return;
  }

  const isFacadeManaged = (el) => Boolean(el.closest("[data-facade-js]"));

  const isInteractionCritical = (el) => {
    if (isFacadeManaged(el)) return false;
    return Boolean(el.closest("dialog, #cart-drawer, [data-section-load='eager']"));
  };

  const viewportTargets = [];
  const eagerTargets = [];

  targets.forEach((el) => {
    if (isFacadeManaged(el)) return;
    if (isInteractionCritical(el)) {
      eagerTargets.push(el);
    } else {
      viewportTargets.push(el);
    }
  });

  eagerTargets.forEach(loadSectionAssets);

  if (!viewportTargets.length || !("IntersectionObserver" in window)) {
    viewportTargets.forEach(loadSectionAssets);
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        loadSectionAssets(entry.target);
        observer.unobserve(entry.target);
      });
    },
    {
      rootMargin: "600px 0px",
      threshold: 0,
    },
  );

  viewportTargets.forEach((el) => observer.observe(el));
})();

/*
  Shopify's errors messages
*/
document.querySelectorAll(".errors").forEach((element) => {
  element.classList.add("alert");
  element.setAttribute("data-alert-type", "error");
});

/*
  Shopify's challenge page
*/
document.querySelector(".btn.shopify-challenge__button")?.classList.add("btn-primary");

/*
  Viewport detection
  Do not read offsetHeight here — that forces layout on every marked
  section before first paint and delays LCP.
*/
(() => {
  const els = document.querySelectorAll("[data-viewport-entered]");
  if (!els.length) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.dataset.viewportEntered = "true";
        observer.unobserve(entry.target);

        if (entry.target.dataset.videoAutoplay === "true") {
          entry.target.querySelectorAll("video").forEach((video) => {
            video.muted = true;
            video.play();
          });
        }
      });
    },
    {
      rootMargin: "0px 0px -30px 0px",
      threshold: 0,
    },
  );

  els.forEach((el) => observer.observe(el));
})();

/*
  YouTube/Vimeo click-to-load facade
*/
const withAutoplay = (url) => {
  try {
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.set("autoplay", "1");
    return parsed.toString();
  } catch (error) {
    return url.includes("?") ? `${url}&autoplay=1` : `${url}?autoplay=1`;
  }
};

document.addEventListener("click", (event) => {
  const playButton = event.target.closest("[data-video-facade-play]");
  if (!playButton) return;

  const facade = playButton.closest("[data-video-facade]");
  if (!facade || facade.dataset.loaded === "true") return;

  const template = facade.querySelector("template");
  if (!template) return;

  const iframe = template.content.querySelector("iframe");
  if (!iframe) return;

  const src = iframe.dataset.src;
  if (!src) return;

  iframe.src = withAutoplay(src);
  iframe.removeAttribute("data-src");
  facade.appendChild(iframe);
  facade.dataset.loaded = "true";
});

/*
  Shopify's money format
*/
window.Shopify.formatMoney = function (cents, format) {
  if (typeof cents === "string") {
    cents = cents.replace(".", "");
  }

  const defaultFormat = window.Shopify.money_format || "${{amount}}";
  const formatString = format || defaultFormat;

  const formatWithDelimiters = (number, precision = 2, thousands = ",", decimal = ".") => {
    if (isNaN(number) || number == null) return "0";

    number = (number / 100).toFixed(precision);
    const [integerPart, decimalPart] = number.split(".");
    const formattedInt = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);

    return decimalPart ? `${formattedInt}${decimal}${decimalPart}` : formattedInt;
  };

  const placeholderMatch = formatString.match(/{{\s*(\w+)\s*}}/);
  if (!placeholderMatch) return cents;

  const placeholder = placeholderMatch[1];
  let value;

  switch (placeholder) {
    case "amount":
      value = formatWithDelimiters(cents, 2);
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0);
      break;
    case "amount_with_comma_separator":
      value = formatWithDelimiters(cents, 2, ".", ",");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ".", ",");
      break;
    default:
      value = formatWithDelimiters(cents, 2);
  }

  return formatString.replace(placeholderMatch[0], value);
};

// Resize images
window.Shopify.resizeImage = function (src, size, crop = "") {
  if (src)
    return src
      .replace(
        /_(pico|icon|thumb|small|compact|medium|large|grande|original|1024x1024|2048x2048|master)+\./g,
        "."
      )
      .replace(/\.jpg|\.png|\.gif|\.jpeg/g, (match) => {
        if (crop.length) {
          crop = `_crop_${crop}`;
        }
        return `_${size}${crop}${match}`;
      });
  else
    return "data:image/svg+xml,%3Csvg class='placeholder-svg' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 525.5 525.5'%3E%3Cpath d='M324.5 212.7H203c-1.6 0-2.8 1.3-2.8 2.8V308c0 1.6 1.3 2.8 2.8 2.8h121.6c1.6 0 2.8-1.3 2.8-2.8v-92.5c0-1.6-1.3-2.8-2.9-2.8zm1.1 95.3c0 .6-.5 1.1-1.1 1.1H203c-.6 0-1.1-.5-1.1-1.1v-92.5c0-.6.5-1.1 1.1-1.1h121.6c.6 0 1.1.5 1.1 1.1V308z'%3E%3C/path%3E%3Cpath d='M210.4 299.5H240v.1s.1 0 .2-.1h75.2v-76.2h-105v76.2zm1.8-7.2l20-20c1.6-1.6 3.8-2.5 6.1-2.5s4.5.9 6.1 2.5l1.5 1.5 16.8 16.8c-12.9 3.3-20.7 6.3-22.8 7.2h-27.7v-5.5zm101.5-10.1c-20.1 1.7-36.7 4.8-49.1 7.9l-16.9-16.9 26.3-26.3c1.6-1.6 3.8-2.5 6.1-2.5s4.5.9 6.1 2.5l27.5 27.5v7.8zm-68.9 15.5c9.7-3.5 33.9-10.9 68.9-13.8v13.8h-68.9zm68.9-72.7v46.8l-26.2-26.2c-1.9-1.9-4.5-3-7.3-3s-5.4 1.1-7.3 3l-26.3 26.3-.9-.9c-1.9-1.9-4.5-3-7.3-3s-5.4 1.1-7.3 3l-18.8 18.8V225h101.4z'%3E%3C/path%3E%3Cpath d='M232.8 254c4.6 0 8.3-3.7 8.3-8.3s-3.7-8.3-8.3-8.3-8.3 3.7-8.3 8.3 3.7 8.3 8.3 8.3zm0-14.9c3.6 0 6.6 2.9 6.6 6.6s-2.9 6.6-6.6 6.6-6.6-2.9-6.6-6.6 3-6.6 6.6-6.6z'%3E%3C/path%3E%3C/svg%3E";
};


/*
  Header
*/
class ThemeHeader extends HTMLElement {
  constructor() {
    super();
    
    this.headerGroup = document.querySelector("#header-group");
    this.stickyHeader();
    this.mobileMenu();
    this.desktopMenu();
    this.transparentHeader();
  }

  stickyHeader() {
    switch(this.dataset.stickyHeader) {
      case "none":
        return;
      case "scroll-up": {
          this.headerGroup.classList.add("sticky");
          let lastScrollTop = 0;
          let ticking = false;

          window.addEventListener("scroll", () => {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(() => {
              const currentScroll = window.pageYOffset;
              if (currentScroll > lastScrollTop && currentScroll > this.headerGroup.clientHeight) {
                this.headerGroup.classList.add("hide");
              } else {
                this.headerGroup.classList.remove("hide");
              }
              lastScrollTop = currentScroll <= 0 ? 0 : currentScroll;
              ticking = false;
            });
          }, { passive: true });
          break;
        }
      case "always": {
          this.headerGroup.classList.add("sticky");
          break;
        }
      default:
      break;
    }
  }

  mobileMenu() {
    const toggler = this.querySelector('[aria-controls="header-mobile-menu"]');
    const menu = this.querySelector("#header-mobile-menu");
    const nav = this.querySelector("#header-mobile-nav");

    const openMenu = () => {
      toggler.setAttribute("aria-expanded", "true");
      menu.hidden = false;
      menu.nextElementSibling.hidden = false;
      document.body.style.overflow = "hidden";

      const top = document.querySelector("#header-group.sticky")?.offsetHeight || 0;
      menu.style.setProperty("--top", `${top}px`);
      menu.nextElementSibling.style.setProperty("--top", `${top}px`);

      document.addEventListener("keydown", handleFocus);
      document.addEventListener("click", handleOutsideClick);
    };

    const closeMenu = () => {
      menu.classList.add("closing");
      menu.addEventListener(
        "animationend",
        () => {
          toggler.setAttribute("aria-expanded", "false");
          menu.classList.remove("closing");
          menu.hidden = true;
          menu.nextElementSibling.hidden = true;
          document.body.style.overflow = "";

          menu.style.removeProperty("--top");
          menu.nextElementSibling.style.removeProperty("--top");

          nav.querySelectorAll('[aria-expanded="true"]').forEach((link) => link.setAttribute("aria-expanded", "false"));
          document.removeEventListener("keydown", handleFocus);
          document.removeEventListener("click", handleOutsideClick);
          if (menu.contains(document.activeElement) || document.activeElement === document.body) {
            toggler.focus();
          }
        },
        { once: true },
      );
    };

    nav.querySelectorAll("a[aria-expanded]").forEach((link) => {
      const parentLink = link.closest(".mobile-nav-dropdown")?.previousElementSibling;

      link.addEventListener("click", async (event) => {
        event.preventDefault();
        await new Promise((resolve) => setTimeout(resolve, 150));

        if (link.classList.contains("has-arrow-left")) {
          parentLink.setAttribute("aria-expanded", "false");
        } else {
          link.setAttribute("aria-expanded", "true");
        }
      });
    });

    const handleFocus = (event) => {
      if (event.key === "Tab") {
        if (document.activeElement.closest(".mobile-nav-dropdown")) {
          const dropdown = document.activeElement.closest(".mobile-nav-dropdown");
          const focusableElements = dropdown.querySelectorAll("a, button");
          const firstElement = focusableElements[0];
          const lastElement = focusableElements[focusableElements.length - 1];

          if (document.activeElement === lastElement && !event.shiftKey) {
            event.preventDefault();
            firstElement.focus();
          } else if (document.activeElement === firstElement && event.shiftKey) {
            event.preventDefault();
            lastElement.focus();
          }
        } else {
          const focusableElements = menu.querySelectorAll("a, button");
          const firstElement = focusableElements[0];
          const lastElement = focusableElements[focusableElements.length - 1];

          if (document.activeElement === toggler) {
            if (event.shiftKey) {
              event.preventDefault();
              lastElement.focus();
            } else {
              event.preventDefault();
              firstElement.focus();
            }
          } else if (document.activeElement === lastElement && !event.shiftKey) {
            event.preventDefault();
            toggler.focus();
          } else if (document.activeElement === firstElement && event.shiftKey) {
            event.preventDefault();
            toggler.focus();
          }
        }
      }

      if (event.key === "Enter" || event.key === " ") {
        if (document.activeElement.classList.contains("has-arrow-left")) {
          const parentLink = document.activeElement.closest(".mobile-nav-dropdown").previousElementSibling;
          parentLink.setAttribute("aria-expanded", "false");
          parentLink.focus();
        } else if (document.activeElement.classList.contains("has-arrow-right")) {
          document.activeElement.setAttribute("aria-expanded", "true");
          const dropdown = document.activeElement.nextElementSibling;
          setTimeout(() => dropdown.querySelector("a").focus(), 200);
        }
      }

      if (event.key === "Escape") {
        if (document.activeElement.closest(".mobile-nav-dropdown")) {
          document.activeElement.closest(".mobile-nav-dropdown").previousElementSibling.setAttribute("aria-expanded", "false");
          document.activeElement.closest(".mobile-nav-dropdown").previousElementSibling.focus();
        } else {
          closeMenu();
          toggler.focus();
        }
      }
    };

    const handleOutsideClick = (event) => {
      if (document.querySelector("#header-mobile").contains(event.target) && !toggler.contains(event.target)) {
        closeMenu();
      }
    };

    toggler.addEventListener("click", (event) => {
      event.preventDefault();
      const isExpanded = toggler.getAttribute("aria-expanded") === "true";
      isExpanded ? closeMenu() : openMenu();
    });

    document.addEventListener("shopify:block:select", (event) => {
      if (event.target.querySelector("#header-mobile-menu")) {
        openMenu();
      }
    });
  }

  desktopMenu() {
    this.querySelectorAll("#header-desktop .dropdown-btn").forEach((link) => {
      link.addEventListener("dropdown:open", () => {
        document.body.setAttribute("data-header-dropdown-open", "");
      });
      link.addEventListener("dropdown:close", () => {
        setTimeout(() => {
          if (!this.querySelector('#header-desktop .dropdown-btn[aria-expanded="true"]')) {
            document.body.removeAttribute("data-header-dropdown-open");
          }
        }, 100);
      });
    });
  }

  transparentHeader() {
    const hasTransparentHeader = this.headerGroup?.dataset.transparentHeader === "true";
    if (!hasTransparentHeader) return;

    let transparentTicking = false;
    window.addEventListener("scroll", () => {
      if (transparentTicking) return;
      transparentTicking = true;
      requestAnimationFrame(() => {
        if (window.pageYOffset > this.headerGroup.clientHeight) {
          this.headerGroup.setAttribute("data-transparent-header", "false");
          this.headerGroup.style.position = "fixed";
          this.headerGroup.style.width = "100%";
        } else if (!document.querySelector('#header-desktop .dropdown-btn[aria-expanded="true"]')) {
          this.headerGroup.setAttribute("data-transparent-header", "true");
          this.headerGroup.style.position = "";
          this.headerGroup.style.width = "";
        }
        transparentTicking = false;
      });
    }, { passive: true });

    document.querySelectorAll("#header-desktop .dropdown-btn").forEach((link) => {
      link.addEventListener("dropdown:open", () => {
        if (window.pageYOffset <= this.headerGroup.clientHeight) {
          this.headerGroup.setAttribute("data-transparent-header", "false");
          this.headerGroup.style.position = "fixed";
          this.headerGroup.style.width = "100%";
        }
      });
      link.addEventListener("dropdown:close", () => {
        setTimeout(() => {
          if (window.pageYOffset <= this.headerGroup.clientHeight && !document.querySelector('#header-desktop .dropdown-btn[aria-expanded="true"]')) {
            this.headerGroup.setAttribute("data-transparent-header", "true");
          }
        }, 100);
      });
    });

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.target.getAttribute("aria-expanded") === "true") {
          this.headerGroup.setAttribute("data-transparent-header", "false");
          this.headerGroup.style.position = "fixed";
          this.headerGroup.style.width = "100%";
        } else {
          if (window.pageYOffset <= this.headerGroup.clientHeight) {
            this.headerGroup.setAttribute("data-transparent-header", "true");
          }
        }
      });
    });

    observer.observe(document.querySelector('[aria-controls="header-mobile-menu"]'), {
      attributes: true,
      attributeFilter: ["aria-expanded"]
    });
  }
}
customElements.define("theme-header", ThemeHeader);


/*
  Footer
*/
class ThemeFooter extends HTMLElement {
  constructor() {
    super();

    this.fixedFooter();
  }

  fixedFooter() {
    const hasFixedFooter = this.dataset.fixedDesktop === "true";
    if (!hasFixedFooter) return;

    const setMargin = async () => {
      await new Promise((resolve) => setTimeout(resolve, 500));
      const footerHeight = document.querySelector("#footer-group").offsetHeight;

      if (window.matchMedia("(min-width: 1200px)").matches && footerHeight < window.innerHeight) {
        document.body.classList.add("has-fixed-footer");
        document.querySelector("#main").style.marginBottom = `${footerHeight}px`;
      } else {
        document.body.classList.remove("has-fixed-footer");
        document.querySelector("#main").style.marginBottom = "0";
      }
    };
    setMargin();
    let resizeTimer;
    const debouncedSetMargin = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(setMargin, 150);
    };
    window.addEventListener("resize", debouncedSetMargin);
    window.addEventListener("orientationchange", debouncedSetMargin);

    this.addEventListener('focusin', () => {
      if (document.body.classList.contains("has-fixed-footer")) {
        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
      }
    });
  }
}
customElements.define("theme-footer", ThemeFooter);


/*
  JudgeMe app - Product reviews
*/
(() => {
  const wrapper = document.querySelector(".shopify-app-block[data-block-handle='review_widget']");
  if (!wrapper) return;

  wrapper.classList.add('container')
})();

/*
  License marker check — if bolt-license snippet is removed in the theme editor,
  blank the editor body so the gate cannot be bypassed by deleting the snippet.
*/
(() => {
  const LICENSE_ENFORCEMENT = true;
  if (!LICENSE_ENFORCEMENT) return;
  if (!(window.Shopify && window.Shopify.designMode)) return;
  if (!document.querySelector("[data-license-warning]")) {
    document.body.remove();
  }
})();

/*
  Theme info
*/
console.info(
  "%cBolt theme%c v3.1.1 %cby GeniusMakerAcademy%c https://geniusmakerthemes.com",
  "background:#020617;color:#f9fafb;padding:2px 8px;border-radius:4px;font-weight:700;",
  "font-weight:700;",
  "",
  ""
);
